/** Original compressed neighborhood navigation. Units are scene metres. */
export type Point = { x: number; z: number };
export type Rect = { minX: number; maxX: number; minZ: number; maxZ: number };
export const MAP_BOUNDS = { minX: -25.5, maxX: 25.5, minZ: -16, maxZ: 12.2 };
export const WALKABLE: Rect[] = [
  { minX: -16, maxX: 24.8, minZ: -4.2, maxZ: 6.2 },
  { minX: -25.4, maxX: 25.4, minZ: 5.4, maxZ: 12 },
  { minX: -25.4, maxX: -23.4, minZ: -15.8, maxZ: 12 },
  { minX: 22.1, maxX: 25.4, minZ: -15.8, maxZ: 12 },
  { minX: -25.4, maxX: 25.4, minZ: -15.8, maxZ: -14.1 },
];
export const OBSTACLES: Rect[] = [
  { minX: -24.3, maxX: -17.4, minZ: -1.6, maxZ: 5.4 },
  { minX: 0.3, maxX: 3.8, minZ: -1.7, maxZ: 1.7 },
  { minX: 9.4, maxX: 11.1, minZ: 0.9, maxZ: 2.6 },
  { minX: 15.9, maxX: 17.5, minZ: 0.2, maxZ: 1.8 },
  { minX: -9.8, maxX: -6, minZ: 8.6, maxZ: 10.4 },
];
export function walkable(x: number, z: number, r = 0.28) {
  return (
    Number.isFinite(x) &&
    Number.isFinite(z) &&
    WALKABLE.some((b) => x >= b.minX + r && x <= b.maxX - r && z >= b.minZ + r && z <= b.maxZ - r) &&
    !OBSTACLES.some((b) => x + r > b.minX && x - r < b.maxX && z + r > b.minZ && z - r < b.maxZ)
  );
}
export function segmentClear(a: Point, b: Point) {
  const steps = Math.ceil(Math.hypot(b.x - a.x, b.z - a.z) / 0.2);
  for (let i = 0; i <= steps; i++) {
    const t = steps ? i / steps : 0;
    if (!walkable(a.x + (b.x - a.x) * t, a.z + (b.z - a.z) * t)) return false;
  }
  return true;
}
const STEP = 0.5,
  WIDTH = 103,
  HEIGHT = 57;
const grid = new Uint8Array(WIDTH * HEIGHT);
for (let iz = 0; iz < HEIGHT; iz++)
  for (let ix = 0; ix < WIDTH; ix++)
    grid[iz * WIDTH + ix] = walkable(ix * STEP - 25.5, iz * STEP - 16) ? 1 : 0;
const point = (index: number): Point => ({
  x: (index % WIDTH) * STEP - 25.5,
  z: Math.floor(index / WIDTH) * STEP - 16,
});
function nearest(p: Point) {
  let best = -1,
    distance = Infinity;
  for (let dz = -2; dz <= 2; dz++)
    for (let dx = -2; dx <= 2; dx++) {
      const ix = Math.round((p.x + 25.5) / STEP) + dx,
        iz = Math.round((p.z + 16) / STEP) + dz;
      if (ix < 0 || ix >= WIDTH || iz < 0 || iz >= HEIGHT) continue;
      const i = iz * WIDTH + ix;
      if (!grid[i]) continue;
      const q = point(i),
        d = Math.hypot(q.x - p.x, q.z - p.z);
      if (d < distance && segmentClear(p, q)) {
        best = i;
        distance = d;
      }
    }
  return best;
}
/** Deterministic bounded grid search; path validity is checked on the server. */
export function findPath(a: Point, b: Point): Point[] | null {
  if (!walkable(a.x, a.z) || !walkable(b.x, b.z)) return null;
  if (segmentClear(a, b)) return [a, b];
  const start = nearest(a),
    end = nearest(b);
  if (start < 0 || end < 0) return null;
  const previous = new Int32Array(grid.length);
  previous.fill(-1);
  const queue = new Int32Array(grid.length);
  let head = 0,
    tail = 0;
  queue[tail++] = start;
  previous[start] = start;
  while (head < tail && previous[end] === -1) {
    const i = queue[head++]!,
      x = i % WIDTH,
      z = Math.floor(i / WIDTH);
    for (const [dx, dz] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      const nx = x + dx!,
        nz = z + dz!;
      if (nx < 0 || nx >= WIDTH || nz < 0 || nz >= HEIGHT) continue;
      const n = nz * WIDTH + nx;
      if (!grid[n] || previous[n] !== -1) continue;
      previous[n] = i;
      queue[tail++] = n;
    }
  }
  if (previous[end] === -1) return null;
  const raw: Point[] = [b];
  let i = end;
  while (i !== start) {
    raw.push(point(i));
    i = previous[i]!;
  }
  raw.push(point(start), a);
  raw.reverse();
  const simplified: Point[] = [a];
  let anchor = 0;
  while (anchor < raw.length - 1) {
    let next = raw.length - 1;
    while (next > anchor + 1 && !segmentClear(raw[anchor]!, raw[next]!)) next--;
    simplified.push(raw[next]!);
    anchor = next;
  }
  return simplified;
}
export const pathLength = (path: Point[]) =>
  path.reduce((sum, p, i) => (i ? sum + Math.hypot(p.x - path[i - 1]!.x, p.z - path[i - 1]!.z) : 0), 0);
export function samplePath(path: Point[], fraction: number): Point {
  if (!path.length) return { x: 0, z: 0 };
  let left = pathLength(path) * Math.max(0, Math.min(1, fraction));
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!,
      b = path[i]!,
      length = Math.hypot(b.x - a.x, b.z - a.z);
    if (left <= length) {
      const t = length ? left / length : 1;
      return { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
    }
    left -= length;
  }
  return path[path.length - 1]!;
}
